"use client";

import Link from "next/link";
import Image from "next/image";
import { useIntl } from "react-intl";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { appTargetOfUrl, useAppLink } from "@/lib/analytics/app-links";
import { DEMO_URL as DEFAULT_DEMO_URL } from "@/lib/demo";
import { LoopVideo } from "./LoopVideo";

type AppCtaProps = {
  title?: string;
  subtitle?: string;
  image: string;
  alt?: string;
  badge?: string;
  button?: string;
  url?: string;
  demo?: string;
  demoUrl?: string;
};

export function AppCta({
  title,
  subtitle,
  image,
  alt,
  badge,
  button,
  url,
  demo,
  demoUrl,
}: AppCtaProps) {
  const intl = useIntl();
  const { href: appUrl, plain: plainUrl, target: appTarget } = useAppLink("article", {
    webBase: url || undefined,
  });

  if (!image) return null;

  const isExternal = /^https?:\/\//.test(appUrl);
  const isGif = image.toLowerCase().endsWith(".gif");
  const isVideo = /\.(mp4|webm)$/i.test(image);
  const buttonLabel = button?.trim() || intl.formatMessage({ id: "ressources.cta.default" });
  const demoLabel = demo?.trim();
  const demoHref = demoUrl?.trim() || DEFAULT_DEMO_URL;

  return (
    <div className="not-prose my-10 flex flex-col gap-6 overflow-hidden rounded-3xl border border-gray-light bg-surface p-6 sm:p-8 md:flex-row md:items-center md:gap-10">
      {/* Left: text + actions */}
      <div className="flex flex-1 flex-col items-start gap-3">
        {badge && (
          <span className="rounded-full bg-blue-ulight px-3 py-1 text-xs font-semibold text-brand-ink">
            {badge}
          </span>
        )}
        {title && (
          <p className="text-xl font-bold leading-tight text-text-main sm:text-2xl">{title}</p>
        )}
        {subtitle && <p className="text-sm leading-relaxed text-gray-main">{subtitle}</p>}
        <div className="mt-1 flex flex-col items-start gap-2">
          <Link
            href={appUrl}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={() => {
              trackEvent("ressources-article-cta", { url: plainUrl }, { criticalOnNav: true });
              if (appTargetOfUrl(appUrl)) trackCtaClick("article", appTarget);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-main px-5 py-2.5 text-sm font-semibold text-white no-underline transition-all duration-200 hover:scale-105 hover:bg-[#4F46E5]"
          >
            {buttonLabel}
          </Link>
          {demoLabel && (
            <Link
              href={demoHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("ressources-article-demo", { demoHref });
                trackCtaClick("article", "demo");
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-sec bg-surface px-5 py-2.5 text-sm font-semibold text-text-main no-underline transition-all duration-200 hover:scale-105 hover:bg-gray-ulight"
            >
              {demoLabel}
            </Link>
          )}
        </div>
      </div>

      {/* Right: preview */}
      <span className="relative block aspect-video w-full shrink-0 overflow-hidden rounded-2xl border border-gray-light bg-gray-ulight md:w-3/5">
        {isVideo ? (
          <LoopVideo
            src={image}
            label={alt || title}
            wrapperClassName="absolute inset-0"
            className="h-full w-full object-cover"
          />
        ) : (
          <Image src={image} alt={alt || title || ""} fill unoptimized={isGif} className="object-cover" />
        )}
      </span>
    </div>
  );
}
